import React, { useEffect, useMemo, useReducer, useState } from "react";
import AnimateHeight from "react-animate-height";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { familySteps } from "../../data/family-steps";
import { mapRegion } from "../../mappers/calculation-mapper";
import { mapFormError } from "../../mappers/form-error-mapper";
import { mapStepsConfig } from "../../mappers/step-config-mapper";
import { Region } from "../../models/calculation-api.types";
import {
  CalculatorV2ConfigurationModel,
  CalculatorV2RegionConfigurationModel,
} from "../../models/calculator-configuration";
import { CalculatorTranslations } from "../../models/CalculatorTranslations";
import { FamilyFormState } from "../../models/FamilyFormState";
import { childrenReducer } from "../../reducers/children-reducer";
import { CalculatorService } from "../../services/calculator-service";
import { AddChildModal } from "../components/AddChild.Modal";
import { CalculationResults } from "../components/CalculationResult";
import { CalculatorSection } from "../components/CalculationSection";
import { LazyLoader } from "../components/LazyLoader";
import { Question } from "../components/Question";
import axios from "axios";
import { CTAButtons } from "../components/CTAButtons";

declare var dataLayer: any;

export type CalculatorPageProps = {
  config: CalculatorV2ConfigurationModel;
  personaID: string;
  translations: CalculatorTranslations;
  apiUrl: string;
};

export default function CalculatorPage({ config, personaID, translations, apiUrl }: CalculatorPageProps) {
  const resourceStringKey = "Infino.CalculatorV2.";
  const resultTranslations = useQuery<{ [key: string]: string }>(`ResourceStrings:${resourceStringKey}`, async () => {
    let api = axios.create({ baseURL: "" });
    const data = await api.get<{ [key: string]: string }>(
      `/ResourceString/GetResourceStringsByFilter?filter=${resourceStringKey}`
    );
    const result: { [key: string]: string } = {};
    for (const key in data.data) {
      result[key.split(resourceStringKey)[1]] = data.data[key];
    }
    return result;
  });

  // calculator query api
  const mutation = useMutation(CalculatorService.calculate, {
    onSuccess: () => {
      if (dataLayer) {
        dataLayer.push({ event: "calculator" });
      }
    },
  });

  // region config selection (default current persona, this switches the entire config)
  const [regionPersona, setRegionPersona] = useState(personaID);
  const [regionConfig, region] = useMemo(() => getRegionConfig(config, regionPersona), [config, regionPersona]);

  // children state
  const steps = mapStepsConfig(familySteps, regionConfig);
  const [state, dispatch] = useReducer(childrenReducer, {
    showModal: false,
    children: [],
  });

  // calculator form + submitting
  const form = useForm<FamilyFormState>({
    mode: "onChange",
    defaultValues: {
      Region: region.toLowerCase(),
    },
  });
  const { handleSubmit } = form;

  const onSubmit = handleSubmit(
    function (data) {
      console.debug("calculator submit success:", data, state.children);
      mutation.mutate({
        family: data,
        children: state.children,
        apiUrl: apiUrl,
      });
    },
    function (errors) {
      console.debug("calculator submit error:", errors);
    }
  );

  const regionValue = form.getValues("Region");

  // update regionConfig if familystate sets a region
  useEffect(() => {
    console.debug("region use effect", regionValue);
    if (regionValue) {
      const mappedRegion = mapRegion(regionValue);
      switch (mappedRegion) {
        case Region.Flanders:
          if (config.Flanders) {
            setRegionPersona(config.Flanders.Persona);
          }
          break;
        case Region.Brussels:
          if (config.Brussels) {
            setRegionPersona(config.Brussels.Persona);
          }
          break;
        case Region.Wallonia:
          if (config.Wallonia) {
            setRegionPersona(config.Wallonia.Persona);
          }
          break;
      }
    }
  }, [regionValue, setRegionPersona]);

  // set children field so that its also validated by react-hook-form
  const [initial, setInitial] = useState(true);
  useEffect(() => {
    if (initial) {
      setInitial(false);
      return;
    }
    form.setValue("Children", state.children as never, {
      shouldValidate: true,
    });
  }, [state]);

  form.register("Children", { required: true, minLength: 1 });

  return (
    <div className="container">
      <div className="row">
        <FormProvider {...form}>
          <div className="col-12 col-md-8 offset-md-2 bg-white card-slope-top p-0">
            <div className="d-flex flex-column">
              <form onSubmit={onSubmit}>
                {steps.map((step, idx) => {
                  return step.field !== "Children" ? (
                    <CalculatorSection
                      counter={idx + 1}
                      title={step.title}
                      description={step.description}
                      key={step.field}>
                      {step.questions.map((question) => (
                        <Question key={question.field} question={question} translations={translations} />
                      ))}
                    </CalculatorSection>
                  ) : (
                    <CalculatorSection
                      key={step.field}
                      counter={steps.length}
                      title={step.title}
                      description={step.description}>
                      {state.children.map((child, idx) => (
                        <div key={child.id}>
                          {idx > 0 ? <div className="border-top border-ecru-dark-25 my-1"></div> : null}
                          <div className="d-flex flex-row pt-1 pb-2 align-items-center">
                            <div className="text-brown-100 text-medium d-flex align-items-center mr-1 mr-md-2">
                              #{idx + 1}
                            </div>
                            <div>{child.ChildName}</div>

                            <div
                              onClick={() =>
                                dispatch({
                                  type: "start_edit",
                                  payload: child.id,
                                })
                              }
                              className="btn btn-link font-weight-light text-underline ml-auto">
                              {translations.Modify}
                            </div>
                            <button
                              type="button"
                              className="btn btn-link"
                              onClick={() => dispatch({ type: "delete", payload: child.id })}
                              aria-label="delete">
                              <img src="/img/svg/icon-trash.svg" alt="" width="16" />
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="align-items-center btn d-flex font-weight-bold pl-0 text-medium text-primary"
                        onClick={() => dispatch({ type: "start_add" })}
                        aria-label="add">
                        <img className="mr-2" src="/img/svg/icon-plus.svg" alt="" width="16" />
                        <u>{translations.AddChild}</u>
                      </button>

                      {form.formState.isSubmitted && form.formState.errors[step.field] && (
                        <div>
                          <span className="text-red text-small mb-3">
                            {mapFormError(form.formState.errors[step.field]?.type, translations)}
                            <br />
                          </span>
                        </div>
                      )}
                    </CalculatorSection>
                  );
                })}

                <CalculatorSection fullWidth>
                  <button type="submit" className="btn btn-primary w-100" disabled={mutation.isLoading}>
                    {translations.Calculate}
                  </button>

                  {mutation.isLoading && (
                    <LazyLoader>
                      <div className="d-flex justify-content-center align-items-center py-5">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    </LazyLoader>
                  )}

                  {mutation.isSuccess && (
                    <div className="mt-3">
                      <CalculationResults model={mutation.data} translations={resultTranslations.data || {}} />
                      <CTAButtons region={region} config={config} translations={resultTranslations.data || {}} />
                      <div className="row">
                        <div className="col-12">
                          {translations.CalculationDisclaimerTitle ? (
                            <div className="text-primary font-weight-bold mb-2">
                              {translations.CalculationDisclaimerTitle}
                            </div>
                          ) : null}
                          <div
                            dangerouslySetInnerHTML={{
                              __html: translations.CalculationDisclaimerText,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </CalculatorSection>
              </form>
            </div>
          </div>
        </FormProvider>
      </div>
      {state.showModal && (
        <AddChildModal
          translations={translations}
          config={regionConfig.Steps["Children"]}
          initialState={state.editing}
          region={region}
          onAddChild={(child) => dispatch({ type: "add", payload: child })}
          onClose={() => dispatch({ type: "close" })}></AddChildModal>
      )}
    </div>
  );
}

function getRegionConfig(
  config: CalculatorV2ConfigurationModel,
  personaID: string
): [CalculatorV2RegionConfigurationModel, Region] {
  console.debug("getRegionConfig request:", personaID);
  switch (personaID) {
    case config.Flanders?.Persona:
      console.debug("getRegionConfig", "flanders");
      return [config.Flanders, Region.Flanders];
    case config.Brussels?.Persona:
      console.debug("getRegionConfig", "brussels");
      return [config.Brussels, Region.Brussels];
    case config.Wallonia?.Persona:
      console.debug("getRegionConfig", "wallonia");
      return [config.Wallonia, Region.Wallonia];
    default:
      console.debug("getRegionConfig", "default");
      return [config.Flanders, Region.Flanders];
  }
}
